import { Point } from './Point';

/** flash.geom.Matrix (double precision, like the AS3 class; display objects store float32 internally). */
export class Matrix {
  a: number;
  b: number;
  c: number;
  d: number;
  tx: number;
  ty: number;

  constructor(a: number = 1, b: number = 0, c: number = 0, d: number = 1, tx: number = 0, ty: number = 0) {
    this.a = +a;
    this.b = +b;
    this.c = +c;
    this.d = +d;
    this.tx = +tx;
    this.ty = +ty;
  }

  clone(): Matrix {
    return new Matrix(this.a, this.b, this.c, this.d, this.tx, this.ty);
  }

  /** this = this * m (apply this, then m). */
  concat(m: Matrix): void {
    const a = m.a * this.a + m.c * this.b;
    const b = m.b * this.a + m.d * this.b;
    const c = m.a * this.c + m.c * this.d;
    const d = m.b * this.c + m.d * this.d;
    const tx = m.a * this.tx + m.c * this.ty + m.tx;
    const ty = m.b * this.tx + m.d * this.ty + m.ty;
    this.a = a;
    this.b = b;
    this.c = c;
    this.d = d;
    this.tx = tx;
    this.ty = ty;
  }

  copyFrom(m: Matrix): void {
    this.a = m.a;
    this.b = m.b;
    this.c = m.c;
    this.d = m.d;
    this.tx = m.tx;
    this.ty = m.ty;
  }

  setTo(a: number, b: number, c: number, d: number, tx: number, ty: number): void {
    this.a = a;
    this.b = b;
    this.c = c;
    this.d = d;
    this.tx = tx;
    this.ty = ty;
  }

  identity(): void {
    this.a = 1;
    this.b = 0;
    this.c = 0;
    this.d = 1;
    this.tx = 0;
    this.ty = 0;
  }

  /** Same arithmetic as Flash: a singular matrix produces Infinity/NaN entries (no exception). */
  invert(): void {
    const det = this.a * this.d - this.c * this.b;
    const tx = (this.d * this.tx - this.c * this.ty) / -det;
    const ty = (this.b * this.tx - this.a * this.ty) / det;
    const a = this.d / det;
    const b = this.b / -det;
    const c = this.c / -det;
    const d = this.a / det;
    this.a = a;
    this.b = b;
    this.c = c;
    this.d = d;
    this.tx = tx;
    this.ty = ty;
  }

  rotate(angle: number): void {
    const sin = Math.sin(angle);
    const cos = Math.cos(angle);
    const a = cos * this.a - sin * this.b;
    const b = sin * this.a + cos * this.b;
    const c = cos * this.c - sin * this.d;
    const d = sin * this.c + cos * this.d;
    const tx = cos * this.tx - sin * this.ty;
    const ty = sin * this.tx + cos * this.ty;
    this.a = a;
    this.b = b;
    this.c = c;
    this.d = d;
    this.tx = tx;
    this.ty = ty;
  }

  scale(sx: number, sy: number): void {
    this.a *= sx;
    this.b *= sy;
    this.c *= sx;
    this.d *= sy;
    this.tx *= sx;
    this.ty *= sy;
  }

  translate(dx: number, dy: number): void {
    this.tx += dx;
    this.ty += dy;
  }

  createBox(sx: number, sy: number, rotation: number = 0, tx: number = 0, ty: number = 0): void {
    this.identity();
    this.rotate(rotation);
    this.scale(sx, sy);
    this.translate(tx, ty);
  }

  createGradientBox(width: number, height: number, rotation: number = 0, tx: number = 0, ty: number = 0): void {
    this.createBox(width / 1638.4, height / 1638.4, rotation, tx + width / 2, ty + height / 2);
  }

  transformPoint(p: Point): Point {
    return new Point(this.a * p.x + this.c * p.y + this.tx, this.b * p.x + this.d * p.y + this.ty);
  }

  deltaTransformPoint(p: Point): Point {
    return new Point(this.a * p.x + this.c * p.y, this.b * p.x + this.d * p.y);
  }

  toString(): string {
    return `(a=${this.a}, b=${this.b}, c=${this.c}, d=${this.d}, tx=${this.tx}, ty=${this.ty})`;
  }
}
